import Decimal from 'decimal.js'
import type { MonetaryValue } from '@shared/lib/money/money'
import { currencyFractionDigits } from '@shared/lib/format/currency'

export interface SplitPreviewInput {
  readonly amount: MonetaryValue
  readonly vendepayFee: MonetaryValue
  readonly affiliatePercentage?: string
  readonly coProducerPercentage?: string
}

export interface SplitPreview {
  readonly producer: MonetaryValue
  readonly affiliate: MonetaryValue
  readonly coProducer: MonetaryValue
  readonly vendepayFee: MonetaryValue
  readonly isInvalid: boolean
}

export function computeSplitPreview(input: SplitPreviewInput): SplitPreview {
  const currency = input.amount.currency
  if (currency !== input.vendepayFee.currency) throw new Error('Moedas incompatíveis na prévia.')
  const close = (value: Decimal) =>
    value.toDecimalPlaces(currencyFractionDigits(currency), Decimal.ROUND_HALF_UP)
  const money = (value: Decimal): MonetaryValue => ({ amount: value.toFixed(3), currency })
  const amount = close(new Decimal(input.amount.amount))
  const zero = new Decimal(0)
  const vendepayFee = close(new Decimal(input.vendepayFee.amount))
  const grossAffiliate =
    percentageOrZero(input.affiliatePercentage) > 0
      ? close(amount.mul(input.affiliatePercentage ?? '0').div(100))
      : zero
  const combined =
    percentageOrZero(input.affiliatePercentage) > 0 &&
    percentageOrZero(input.coProducerPercentage) > 0
  const affiliateFee = combined
    ? close(vendepayFee.mul(input.affiliatePercentage ?? '0').div(100))
    : zero
  const affiliate = grossAffiliate.minus(affiliateFee)
  const afterAffiliateAndFee = amount.minus(vendepayFee).minus(affiliate)
  const coProducer =
    percentageOrZero(input.coProducerPercentage) > 0
      ? close(afterAffiliateAndFee.mul(input.coProducerPercentage ?? '0').div(100))
      : zero
  const producer = afterAffiliateAndFee.minus(coProducer)

  return {
    producer: money(producer),
    affiliate: money(affiliate),
    coProducer: money(coProducer),
    vendepayFee: money(vendepayFee),
    isInvalid: producer.isNegative() || coProducer.isNegative() || affiliate.isNegative(),
  }
}

function percentageOrZero(value: string | undefined): number {
  const parsed = Number.parseFloat(value ?? '0')
  return Number.isFinite(parsed) ? parsed : 0
}
